import { useMemo } from "react";
import { motion } from "framer-motion";
import { currentSeasonIndex, SEASONS } from "./SeasonalHero";

const rnd = (i: number, s: number) => { const x = Math.sin(i * 9301 + s * 49297) * 233280; return x - Math.floor(x); };

function Waves({ colors, top = "62%" }: { colors: string[]; top?: string }) {
  return (
    <div className="absolute inset-x-0 bottom-0" style={{ top }}>
      {colors.map((c, i) => (
        <svg key={i} className="bd-wave absolute bottom-0 h-full" style={{ width: "200%", animationDuration: `${38 + i * 14}s`, animationDirection: i % 2 ? "reverse" : "normal", bottom: `${-i * 6}%` }}
          viewBox="0 0 2880 400" preserveAspectRatio="none">
          <path d={`M0 ${70 + i * 40} C 240 ${30 + i * 40} 480 ${110 + i * 40} 720 ${70 + i * 40} S 1200 ${30 + i * 40} 1440 ${70 + i * 40} S 1920 ${110 + i * 40} 2160 ${70 + i * 40} S 2640 ${30 + i * 40} 2880 ${70 + i * 40} V400 H0 Z`} fill={c} />
        </svg>
      ))}
    </div>
  );
}

const Sailboat = ({ className = "", style }: { className?: string; style?: React.CSSProperties }) => (
  <svg viewBox="0 0 120 100" className={className} style={style}>
    <path d="M58 8 L58 70 L18 70 Z" fill="#FFF8EC" stroke="#8C5E58" strokeWidth="1.5" />
    <path d="M62 18 L62 70 L94 70 Z" fill="#F5C98B" stroke="#8C5E58" strokeWidth="1.5" />
    <path d="M10 74 L110 74 L96 92 L24 92 Z" fill="#8C5E58" />
    <line x1="60" y1="4" x2="60" y2="74" stroke="#5B4636" strokeWidth="2.5" />
  </svg>
);

const PARTICLE = [
  { color: "#F4B6C2", shape: "petal" }, { color: "#FCE7A1", shape: "glint" }, { color: "#D97706", shape: "leaf" }, { color: "#FFFFFF", shape: "snow" },
];

function SeasonParticles() {
  const p = PARTICLE[currentSeasonIndex()];
  const items = useMemo(() => Array.from({ length: 18 }, (_, i) => ({ left: rnd(i, 1) * 100, delay: -rnd(i, 2) * 22, dur: 16 + rnd(i, 3) * 14, size: 8 + rnd(i, 4) * 10 })), []);
  return (
    <>
      {items.map((it, i) => (
        <span key={i} className={p.shape === "glint" ? "bd-glint" : "bd-fall"} data-testid={i === 0 ? `backdrop-particle-${p.shape}` : undefined}
          style={{ left: `${it.left}%`, width: it.size, height: p.shape === "snow" || p.shape === "glint" ? it.size * 0.7 : it.size * 0.6, animationDelay: `${it.delay}s`, animationDuration: `${it.dur}s`,
            background: p.color, borderRadius: p.shape === "leaf" ? "0 80% 0 80%" : p.shape === "petal" ? "80% 0 80% 0" : "50%", boxShadow: p.shape === "snow" ? "0 0 4px rgba(148,163,184,.6)" : undefined, top: p.shape === "glint" ? `${55 + rnd(i, 5) * 35}%` : undefined }} />
      ))}
    </>
  );
}

function BoatScene() {
  return (
    <>
      <div className="absolute inset-0 bg-gradient-to-b from-[#FAF7F2] via-[#F6EFE3] to-[#E7EEEC]" />
      <Waves colors={["#D6E6E4", "#C3DAD8", "#AFCDCB"]} top="70%" />
      <div className="bd-sail absolute" style={{ top: "63%" }}><Sailboat className="bd-bob h-20 w-24 sm:h-28 sm:w-32" /></div>
      <SeasonParticles />
    </>
  );
}

const FishShape = ({ color }: { color: string }) => (
  <svg viewBox="0 0 64 32" className="h-full w-full overflow-visible">
    <path className="bd-tail" d="M48 16 L62 5 Q58 16 62 27 Z" fill={color} />
    <path d="M4 16 C14 4 36 2 50 14 L50 18 C36 30 14 28 4 16 Z" fill={color} />
    <path d="M24 9 Q30 4 36 8" fill="none" stroke={color} strokeWidth="3" strokeLinecap="round" opacity=".8" />
    <circle cx="14" cy="14" r="2" fill="#fff" opacity=".7" />
  </svg>
);

function SwimmingFish({ count, colors, minTop = 30, spread = 60 }: { count: number; colors: string[]; minTop?: number; spread?: number }) {
  const fish = useMemo(() => Array.from({ length: count }, (_, i) => ({ top: minTop + rnd(i, 7) * spread, dur: 30 + rnd(i, 8) * 30, delay: -rnd(i, 9) * 50, w: 26 + rnd(i, 10) * 34, rev: i % 3 === 0 })), [count, minTop, spread]);
  return (
    <>
      {fish.map((f, i) => (
        <div key={i} className={f.rev ? "bd-swim-rev absolute" : "bd-swim absolute"} data-testid={i === 0 ? "backdrop-fish" : undefined}
          style={{ top: `${f.top}%`, width: f.w, height: f.w / 2, animationDuration: `${f.dur}s`, animationDelay: `${f.delay}s` }}>
          <div className="h-full w-full" style={{ transform: f.rev ? undefined : "scaleX(-1)" }}>
            <div className="bd-sway h-full w-full" style={{ animationDelay: `${-rnd(i, 19) * 2}s` }}><FishShape color={colors[i % colors.length]} /></div>
          </div>
        </div>
      ))}
    </>
  );
}

function DeepScene() {
  const bubbles = useMemo(() => Array.from({ length: 14 }, (_, i) => ({ left: rnd(i, 11) * 100, s: 4 + rnd(i, 12) * 8, dur: 14 + rnd(i, 13) * 14, delay: -rnd(i, 14) * 20 })), []);
  return (
    <>
      <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, #FBF6EC 0%, #E8F1F0 22%, #BFD9DC 55%, #7FA9B5 82%, #4E7C8D 100%)" }} />
      <div className="bd-sun absolute left-1/2 top-[-18vh] h-[55vh] w-[90vw] -translate-x-1/2" />
      <div className="bd-rays absolute left-1/2 top-[-10vh] h-[90vh] w-[120vw] -translate-x-1/2" />
      <div className="bd-glisten absolute inset-x-0 top-[12%] h-10" />
      <SwimmingFish count={9} colors={["#3D5A80", "#5B7065", "#2A6F97", "#E07A5F"]} minTop={38} spread={55} />
      {bubbles.map((b, i) => <span key={i} className="bd-bubble" style={{ left: `${b.left}%`, width: b.s, height: b.s, animationDuration: `${b.dur}s`, animationDelay: `${b.delay}s` }} />)}
    </>
  );
}

function NetScene() {
  return (
    <>
      <div className="absolute inset-0 bg-gradient-to-b from-[#FFF7E8] via-[#FDF3E4] to-[#DDEEF0]" />
      <div className="absolute right-[6%] top-[8%] h-28 w-28 rounded-full bg-[#FCD68A]/50 blur-2xl" />
      <Waves colors={["#CFE6E8", "#B5D8DC"]} top="58%" />
      <div className="absolute right-[6%] hidden sm:right-[12%] sm:block" style={{ top: "50%" }}>
        <svg viewBox="0 0 200 200" className="bd-net absolute -left-[78px] top-[96px] h-[300px] w-[300px]" data-testid="backdrop-net">
          <defs><pattern id="mesh" width="14" height="14" patternUnits="userSpaceOnUse" patternTransform="rotate(45)"><path d="M0 0 H14 M0 0 V14" stroke="#8C5E58" strokeWidth="1" opacity=".55" /></pattern></defs>
          <path d="M100 6 C 170 40 196 120 180 196 L20 196 C 4 120 30 40 100 6 Z" fill="url(#mesh)" stroke="#8C5E58" strokeWidth="1.5" strokeOpacity=".6" />
          {[30, 70, 110, 150].map((x) => <circle key={x} cx={x + 10} cy="194" r="3.5" fill="#D97706" />)}
        </svg>
        <svg viewBox="0 0 160 120" className="bd-bob relative h-36 w-48" data-testid="backdrop-fisherman">
          <path d="M14 78 L146 78 L128 102 L32 102 Z" fill="#C86D51" />
          <circle cx="70" cy="44" r="8" fill="#8C5E58" />
          <path d="M62 54 Q70 50 78 54 L82 78 L58 78 Z" fill="#3D5A80" />
          <path className="bd-arm" d="M76 58 L58 84" stroke="#8C5E58" strokeWidth="4" strokeLinecap="round" style={{ transformOrigin: "76px 58px" }} />
          <path d="M58 84 L60 118" stroke="#8C5E58" strokeWidth="1.2" strokeDasharray="3 2" />
        </svg>
      </div>
      <SwimmingFish count={10} colors={["#E07A5F", "#F2A541", "#2A9D8F", "#3D7EA6", "#E9C46A"]} minTop={64} spread={30} />
    </>
  );
}

const SEASON_TONES = [
  { bg: ["#FBEFF1", "#EEF6E8", "#FDF6E3"], accents: ["#E8A0B4", "#8DB87A", "#F2C46D", "#C86D51"] },
  { bg: ["#FFF6E0", "#E7F3F6", "#FDEBD3"], accents: ["#F2A541", "#3D7EA6", "#E07A5F", "#2A9D8F"] },
  { bg: ["#FBEEDD", "#F5DFC8", "#F3E9D6"], accents: ["#C86D51", "#D97706", "#8C5E58", "#B7791F"] },
  { bg: ["#EEF2F7", "#E4EBF3", "#F4F1EC"], accents: ["#5E7CA6", "#94A3B8", "#3D5A80", "#B5C7DA"] },
];

function GroupScene() {
  const season = currentSeasonIndex();
  const tone = SEASON_TONES[season];
  const bubbles = useMemo(() => Array.from({ length: 9 }, (_, i) => ({ left: 4 + rnd(i, 15) * 90, dur: 20 + rnd(i, 16) * 12, delay: -rnd(i, 17) * 28, s: 34 + rnd(i, 18) * 30 })), []);
  return (
    <>
      <motion.div className="absolute inset-0" data-testid="backdrop-season-wash" data-season={season}
        animate={{ backgroundColor: [...tone.bg, tone.bg[0]] }} transition={{ duration: 36, repeat: Infinity, ease: "easeInOut" }} />
      <div className="bd-blob absolute left-[-10%] top-[10%] h-[45vh] w-[45vh] rounded-full blur-3xl" style={{ background: tone.accents[0], opacity: 0.22 }} />
      <div className="bd-blob absolute bottom-[-8%] right-[-6%] h-[55vh] w-[55vh] rounded-full blur-3xl" style={{ background: tone.accents[1], opacity: 0.2, animationDelay: "-12s", animationDirection: "reverse" }} />
      <div className="bd-blob absolute right-[25%] top-[35%] h-[30vh] w-[30vh] rounded-full blur-3xl" style={{ background: tone.accents[2], opacity: 0.16, animationDelay: "-6s" }} />
      {bubbles.map((b, i) => {
        const c = tone.accents[i % tone.accents.length];
        return (
          <svg key={i} viewBox="0 0 40 34" className={`bd-talk absolute ${i % 3 ? "hidden sm:block" : ""}`} data-testid={i === 0 ? "backdrop-speech" : undefined}
            style={{ left: `${b.left < 50 ? b.left * 0.3 : 70 + (b.left - 50) * 0.5}%`, width: b.s, animationDuration: `${b.dur}s`, animationDelay: `${b.delay}s` }}>
            <path d="M6 2 H34 A4 4 0 0 1 38 6 V22 A4 4 0 0 1 34 26 H16 L8 32 L10 26 H6 A4 4 0 0 1 2 22 V6 A4 4 0 0 1 6 2 Z" fill={c} opacity=".28" />
            <circle cx="13" cy="14" r="2" fill={c} opacity=".6" /><circle cx="20" cy="14" r="2" fill={c} opacity=".6" /><circle cx="27" cy="14" r="2" fill={c} opacity=".6" />
          </svg>
        );
      })}
    </>
  );
}

function ShoreLayer({ count, over }: { count: number; over?: boolean }) {
  const type = SEASONS[currentSeasonIndex()].particle;
  const items = useMemo(() => Array.from({ length: count }, (_, i) => ({
    left: 2 + rnd(i, over ? 31 : 21) * 96, dur: 16 + rnd(i, over ? 32 : 22) * 14, delay: -rnd(i, over ? 33 : 23) * 30,
    size: 0.7 + rnd(i, 24) * 0.8, sway: (rnd(i, 25) - 0.5) * 140, rest: 6 + rnd(i, 26) * 26,
  })), [count, over]);
  const mote = type === "mote";
  return (
    <div aria-hidden className={`pointer-events-none fixed inset-0 overflow-hidden ${over ? "z-20" : "z-0"}`} data-testid={over ? "shore-season-over" : "shore-season-back"} data-particle={type}>
      {items.map((p, i) => (
        <span key={i} className={`particle particle-${type} ${mote ? "" : "particle-settle"}`}
          style={{ left: `${p.left}%`, animationDelay: `${p.delay}s`, animationDuration: `${p.dur}s`, ["--s" as any]: over ? p.size * 1.15 : p.size, ["--sway" as any]: `${p.sway}px`, ["--rest" as any]: `${p.rest}px`, opacity: over ? 0.75 : undefined }} />
      ))}
    </div>
  );
}

function ShoreScene() {
  return <><ShoreLayer count={16} /><ShoreLayer count={5} over /></>;
}

const SCENES: Record<string, () => JSX.Element> = { boat: BoatScene, deep: DeepScene, net: NetScene, groups: GroupScene };

export function PageBackdrop({ variant }: { variant: string }) {
  if (variant === "shore") return <ShoreScene />;
  const Scene = SCENES[variant];
  return <div aria-hidden className="page-backdrop pointer-events-none fixed inset-0 z-0 overflow-hidden" data-testid={`page-backdrop-${variant}`}><Scene /></div>;
}
